import { Box, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useTranslate } from "src/locales";
import { NursePatient } from "../../models";
import PatientAiAnalysisLoading from "./PatientAiAnalysisLoading";
import PatientAiAnalysisResult from "./PatientAiAnalysisResult";
import { PatientAiAnalysisDrawerProps } from "./types";

const buildMetaLine = (patient: NursePatient | null) => {
  if (!patient) return "";

  const parts = [
    `Dosya : ${patient.upn ?? "-"}`,
    `Protokol : ${patient.protid ?? "-"}`,
    `Oda No: ${patient.bedinfo ?? "-"}`,
  ];

  return parts.join(" - ");
};

export default function PatientAiAnalysisDrawer({
  open,
  patient,
  status,
  report,
  stepFlushPending,
  onStepFlushComplete,
  onClose,
}: Readonly<PatientAiAnalysisDrawerProps>) {
  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          backgroundColor: "rgba(0,0,0,0.4)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "all" : "none",
          transition: "opacity 0.3s ease",
        }}
      />

      <Stack
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          top: isMobile ? "auto" : 0,
          right: 0,
          bottom: 0,
          left: isMobile ? 0 : "auto",
          height: isMobile ? "min(86vh, 820px)" : "100vh",
          width: isMobile ? "100%" : 340,
          zIndex: 1201,
          transform: open
            ? "translate3d(0,0,0)"
            : isMobile
              ? "translate3d(0,100%,0)"
              : "translate3d(340px,0,0)",
          transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: isMobile
            ? "0 -12px 40px rgba(15, 23, 42, 0.24)"
            : "-8px 0 40px rgba(0,0,0,0.3)",
          borderTopLeftRadius: isMobile ? "24px" : 0,
          borderTopRightRadius: isMobile ? "24px" : 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2.5, borderBottom: "1px solid #eef2f6", position: "relative" }}
        >
          {isMobile && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: "50%",
                transform: "translateX(-50%)",
                width: 42,
                height: 5,
                borderRadius: 999,
                backgroundColor: "#d8dee8",
              }}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeRoundedIcon sx={{ color: "#f0a119", fontSize: 22 }} />
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#202939" }}>
              Yapay Zeka Analizi
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: "#a3acb9" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #eef2f6",
            background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 55%)",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#18212f", lineHeight: 1.35, mb: 2 }}>
            {t("patient_ai_analysis.drawer_patient_section_title")}
          </Typography>
          <Box
            sx={{
              borderRadius: 2,
              border: "1px solid #e8eef4",
              bgcolor: "rgba(255,255,255,0.9)",
              px: 1.75,
              py: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#18212f", lineHeight: 1.3 }}>
              {patient ? `${patient.name ?? ""} ${patient.surname ?? ""}`.trim() : "Hasta Secilmedi"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#7d8795", mt: 0.65, lineHeight: 1.45 }}>
              {buildMetaLine(patient)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 3 }}>
          {status === "loading" && (
            <PatientAiAnalysisLoading rushToEnd={Boolean(stepFlushPending)} onRushComplete={onStepFlushComplete} />
          )}
          {report && (status === "streaming" || status === "success") && (
            <PatientAiAnalysisResult report={report} isStreaming={status === "streaming"} />
          )}
        </Box>
      </Stack>
    </>
  );
}
